import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";

export type RevisionSheet = FunctionReturnType<typeof api.projectDocumentPages.listByRevision>[number];
export type SheetPageType = RevisionSheet["pageType"];
export type SheetDiscipline = NonNullable<RevisionSheet["discipline"]>;

export const SHEET_PAGE_TYPES: SheetPageType[] = [
  "plan",
  "detail",
  "elevation",
  "section",
  "legend",
  "schedule",
  "spec",
  "cover",
  "notes",
  "other",
  "unknown",
];

/** "A-101 · First Floor Plan", or whichever part was read; undefined when neither was. */
export function formatSheetLabel(sheet: Pick<RevisionSheet, "sheetNumber" | "sheetTitle">) {
  const parts = [sheet.sheetNumber, sheet.sheetTitle].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

export function useRevisionSheets(revisionId: Id<"projectDocumentRevisions"> | null | undefined) {
  return useQuery(
    convexQuery(api.projectDocumentPages.listByRevision, revisionId ? { revisionId } : "skip")
  );
}

/** Sheet labels keyed by page number, for thumbnails and pagers. */
export function useRevisionSheetLabels(
  revisionId: Id<"projectDocumentRevisions"> | null | undefined
): ReadonlyMap<number, string> | undefined {
  const { data: sheets } = useRevisionSheets(revisionId);
  return useMemo(() => {
    if (!sheets) return undefined;
    const labels = new Map<number, string>();
    for (const sheet of sheets) {
      const label = formatSheetLabel(sheet);
      if (label) labels.set(sheet.pageNumber, label);
    }
    return labels;
  }, [sheets]);
}
